import i18n from "i18next";
import { useEffect } from "react";

import { suggestFaq } from "@/lib/stores/faqStore";

/**
 * 1) Curated rules: error text (toast title + description) -> FAQ item. Keep these specific —
 *    a wrong suggestion is worse than none. Ids are the `id` fields in public/locales/<lng>/faq.json.
 */
const ERROR_RULES: Array<{ test: RegExp; id: string }> = [
  { test: /(open|no active|no open) (a )?shift|shift (is )?(required|not open|closed)/i, id: "pos.shift-open" },
  { test: /insufficient (wallet|balance|funds|credit)/i, id: "pos.wallet" },
  { test: /(mobile|phone|emirates).*(already|exists|duplicate)|duplicate (member|mobile|phone)/i, id: "members.duplicate" },
  { test: /import.*(fail|error)|(upload|commit) failed/i, id: "members.import" },
  { test: /negative adjustments? require|stock\.writeoff/i, id: "inventory.writeoff" },
  { test: /approval required/i, id: "inventory.stocktake-approval" },
];

/** Lowercase and collapse punctuation/whitespace so messages compare reliably. */
const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

// 2) Automatic matching: FAQ answers quote the exact messages users see ("Invalid kiosk URL.", «…»).
//    Any destructive toast containing one of those quoted messages points to that answer, in every module.
const QUOTE = /[“"«]([^“”"«»]{14,160})[”"»]/g;
let quoteIndex: { lng: string; entries: Array<{ phrase: string; id: string }> } | null = null;

function buildQuoteIndex(lng: string) {
  const bundle = (i18n.getResourceBundle?.(lng, "faq") ?? i18n.getResourceBundle?.("en", "faq")) as
    | { modules?: Record<string, { sections?: { items: { id?: string; a: string }[] }[] }> }
    | undefined;
  const entries: Array<{ phrase: string; id: string }> = [];
  for (const m of Object.values(bundle?.modules ?? {})) {
    for (const s of m.sections ?? []) {
      for (const it of s.items) {
        if (!it.id) continue;
        for (const match of it.a.matchAll(QUOTE)) {
          const phrase = norm(match[1]);
          // Only message-like quotes (several words) — never single button labels.
          if (phrase.split(" ").length >= 3) entries.push({ phrase, id: it.id });
        }
      }
    }
  }
  return { lng, entries };
}

function quotedMessageMatch(text: string): string | null {
  const lng = i18n.language || "en";
  if (!quoteIndex || quoteIndex.lng !== lng || quoteIndex.entries.length === 0) quoteIndex = buildQuoteIndex(lng);
  const t = norm(text);
  // Longest phrase first, so the most specific message wins.
  const hit = [...quoteIndex.entries].sort((a, b) => b.phrase.length - a.phrase.length).find((e) => t.includes(e.phrase));
  return hit?.id ?? null;
}

/** Suggests the matching FAQ item (if any) for an error message. Returns true when one matched. */
export function suggestFaqForError(text: string): boolean {
  const id = ERROR_RULES.find((r) => r.test.test(text))?.id ?? quotedMessageMatch(text);
  if (!id) return false;
  suggestFaq(id);
  return true;
}

/**
 * Flow hint: while `active`, the Help button bounces and opens straight to this
 * question. Use in wizards/pages, e.g. useFaqHint("members.sell-plan", open).
 */
export function useFaqHint(id: string, active = true): void {
  useEffect(() => {
    if (active) suggestFaq(id);
  }, [id, active]);
}
